// src/reduxStore/accessLogs/slice.js
import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

/** AccessLogs slice scoped by org + site: data[orgID][siteID] = { data, loading }. Separate trigger
 * (loadAccessLogs, no-op reducer) / Start / Success actions prevent saga loops. */

export const defaultFilters = {
	page: 1,
	pageSize: 15,
	datePreset: 'today',
	personIDs: [],
	deviceIDs: [],
	status: undefined,
	startDate: dayjs().startOf('day').toISOString(),
	endDate: dayjs().endOf('day').toISOString(),
	integrationID: undefined,
	siteID: undefined
};

// The Visitors activity card's filter, in the ActivityFilterDropdown's shape; today and nothing else.
export const VISITOR_ACTIVITY_DEFAULT_FILTERS = Object.freeze({
	status: 'all',
	time: 'today',
	personIDs: Object.freeze([]),
	deviceIDs: Object.freeze([]),
	startDate: null,
	endDate: null
});

const initialState = {
	// Scoped: data[orgID][siteID] = { data: [], loading: false }
	data: {},
	totals: {}, // { [orgID]: number }
	// lastLoadedAt[orgID][siteID]: ms epoch of the last server response, for the refresh control's age.
	// Stamped by the saga, not Date.now() in the reducer, to keep the reducer deterministic.
	lastLoadedAt: {},
	filters: {}, // { [orgID]: { page, pageSize, ... } }
	// dataToday[orgID] = { all, [siteID]: n }, socket-refreshed. dailyByOrg[orgID][siteID] =
	// { 'YYYY-MM-DD': n }: 14-day client-side rollup for the portfolio per-site sparkline + WoW.
	dataToday: {},
	dataTodayLoading: false,
	dailyByOrg: {},
	dailyLoading: false,
	dailyTimeStamp: null,
	// dailySeries[orgID][scopeKey] = { series: { 'YYYY-MM-DD': count }, ts }: last-N-days unlocks
	// for the Sites pulse band sparkline + WoW trend, scoped to the active site selector.
	dailySeries: {},
	dailySeriesLoading: false,
	// Every log in a window, paged through in full (see saga). Shape:
	//   logsWindow[orgID]['<scopeKey>:<days>'] = { logs, total, truncated, ts }
	logsWindow: {},
	logsWindowLoading: false,
	// Kept apart from the shared `error`: the Lockers boot screen gates on this
	// window alone, and an unrelated feed failure must not read as its failure.
	logsWindowError: null,
	// Server-computed stat-card aggregates: stats[orgID]['<kind>:<scopeKey>:<days>'] = { data, loading, error, ts }.
	stats: {},
	// Visitor activity pages: visitorActivity[orgID]['<scope>:<days>:<page>:<size>'] = { rows, total, loading, error, ts }.
	visitorActivity: {},
	// The activity card's page + filters: visitorActivityQuery[orgID] = { scopeKey, page, filters };
	// another site reads the defaults.
	visitorActivityQuery: {},
	syncing: false,
	error: null
};

// Ensures the bucket exists at data[orgID][key]
const ensureBucket = (state, orgID, siteID) => {
	const key = siteID;
	if (!state.data[orgID]) state.data[orgID] = {};
	if (!state.data[orgID][key]) state.data[orgID][key] = { data: [], loading: false };
	return key;
};

const accessLogsSlice = createSlice({
	name: 'accessLogs',
	initialState,
	reducers: {
		// Trigger — saga listens (takeLatest). Reducer is a no-op.
		loadAccessLogs: () => {},

		// Sets loading on the target bucket. No saga listens (prevents loop).
		loadAccessLogsStart(state, action) {
			const { orgID, siteID } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			state.data[orgID][key].loading = true;
			state.error = null;
			// Initialize filters with defaults if not set for this org
			if (orgID && !state.filters[orgID]) {
				state.filters[orgID] = { ...defaultFilters };
			}
		},

		setAccessLogsFilters(state, action) {
			const { orgID, filters, resetPage = true } = action.payload;
			const current = state.filters[orgID] || defaultFilters;
			state.filters[orgID] = {
				...current,
				...filters,
				...(resetPage && !('page' in filters) ? { page: 1 } : {})
			};
		},
		resetAccessLogsFilters(state, action) {
			const { orgID } = action.payload;
			state.filters[orgID] = { ...defaultFilters };
		},

		// Replaces bucket data — backend is source of truth for this scope.
		loadAccessLogsSuccess(state, action) {
			const { orgID, siteID, data, total, loadedAt } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			state.data[orgID][key] = { data, loading: false };
			if (!state.totals[orgID]) state.totals[orgID] = {};
			state.totals[orgID][siteID] = total;
			// Only a SUCCESS moves it: a failed refresh must not show "Updated just now" over stale rows.
			if (loadedAt) {
				if (!state.lastLoadedAt[orgID]) state.lastLoadedAt[orgID] = {};
				state.lastLoadedAt[orgID][siteID] = loadedAt;
			}
			state.syncing = false;
		},

		// Stops loading on the bucket, sets global error.
		loadAccessLogsError(state, action) {
			const { orgID, siteID, error } = action.payload;
			const key = siteID;
			if (state.data[orgID]?.[key]) state.data[orgID][key].loading = false;
			state.syncing = false;
			state.error = error;
		},

		// Today counts — per-site activity count for today.
		loadTodayCounts: () => {},
		loadTodayCountsStart(state) {
			state.dataTodayLoading = true;
		},
		loadTodayCountsSuccess(state, action) {
			const { orgID, counts } = action.payload;
			state.dataToday[orgID] = counts;
			state.dataTodayLoading = false;
		},
		loadTodayCountsError(state) {
			state.dataTodayLoading = false;
		},

		// Daily rollup (portfolio sparklines + WoW deltas)
		loadAccessLogDailyRollup(state) {
			state.dailyLoading = true;
		},
		loadAccessLogDailyRollupSuccess(state, action) {
			const { orgID, bySite } = action.payload;
			state.dailyByOrg[orgID] = bySite;
			state.dailyLoading = false;
			state.dailyTimeStamp = Date.now();
		},
		loadAccessLogDailyRollupError(state, action) {
			state.dailyLoading = false;
			state.error = action.payload;
		},

		// Scope-keyed daily series (pulse band sparkline + WoW). Trigger payload
		// is the scopeKey ('all' | siteID); saga listens.
		loadDailySeries(state) {
			state.dailySeriesLoading = true;
		},
		loadDailySeriesSuccess(state, action) {
			const { orgID, scopeKey, series } = action.payload;
			if (!state.dailySeries[orgID]) state.dailySeries[orgID] = {};
			state.dailySeries[orgID][scopeKey] = { series, ts: Date.now() };
			state.dailySeriesLoading = false;
		},
		loadDailySeriesError(state, action) {
			state.dailySeriesLoading = false;
			state.error = action.payload;
		},

		// Every log in a window, paged through in full. Trigger payload is
		// { scopeKey, days }; saga listens and caches per '<scopeKey>:<days>'.
		loadLogsWindow(state) {
			state.logsWindowLoading = true;
			state.logsWindowError = null;
		},
		loadLogsWindowSuccess(state, action) {
			const { orgID, cacheKey, logs, total, truncated = false } = action.payload;
			if (!state.logsWindow[orgID]) state.logsWindow[orgID] = {};
			state.logsWindow[orgID][cacheKey] = { logs, total: total ?? logs.length, truncated, ts: Date.now() };
			state.logsWindowLoading = false;
			state.logsWindowError = null;
		},
		loadLogsWindowError(state, action) {
			state.logsWindowLoading = false;
			state.logsWindowError = action.payload;
			state.error = action.payload;
		},

		// Stat-card aggregates. Trigger payload is { kind: 'people' | 'visitors', scopeKey, days }; saga listens.
		loadLogsStats: () => {},
		loadLogsStatsStart(state, action) {
			const { orgID, cacheKey } = action.payload;
			if (!state.stats[orgID]) state.stats[orgID] = {};
			const prev = state.stats[orgID][cacheKey];
			state.stats[orgID][cacheKey] = {
				data: prev?.data ?? null,
				loading: true,
				error: null,
				ts: prev?.ts ?? null
			};
		},
		loadLogsStatsSuccess(state, action) {
			const { orgID, cacheKey, data } = action.payload;
			if (!state.stats[orgID]) state.stats[orgID] = {};
			// Invalidated mid-flight: this response predates the mutation, so it lands already stale.
			const invalidated = state.stats[orgID][cacheKey]?.invalidated;
			state.stats[orgID][cacheKey] = { data, loading: false, error: null, ts: invalidated ? null : Date.now() };
		},
		loadLogsStatsError(state, action) {
			const { orgID, cacheKey, error } = action.payload;
			if (!state.stats[orgID]) state.stats[orgID] = {};
			state.stats[orgID][cacheKey] = { ...state.stats[orgID][cacheKey], loading: false, error };
		},
		// Marks these kinds stale (data kept): only cards on screen refetch, so a mutation elsewhere costs nothing.
		invalidateLogsStats(state, action) {
			const { orgID, kinds } = action.payload;
			Object.entries(state.stats[orgID] || {}).forEach(([cacheKey, entry]) => {
				if (!kinds.includes(cacheKey.split(':')[0])) return;
				entry.ts = null;
				if (entry.loading) entry.invalidated = true;
			});
		},

		// One page of the visitor activity feed. Trigger payload is { scopeKey, params, force? } (visitorActivityParams).
		loadVisitorActivity: () => {},
		// Sets { page?, filters? } for this scope; new filters start again at page 1.
		setVisitorActivityQuery(state, action) {
			const { orgID, scopeKey, page, filters } = action.payload;
			const prev = state.visitorActivityQuery[orgID];
			const same = prev?.scopeKey === scopeKey;
			state.visitorActivityQuery[orgID] = {
				scopeKey,
				filters: filters ?? (same ? prev.filters : VISITOR_ACTIVITY_DEFAULT_FILTERS),
				page: filters ? 1 : (page ?? (same ? prev.page : 1))
			};
		},
		loadVisitorActivityStart(state, action) {
			const { orgID, cacheKey } = action.payload;
			if (!state.visitorActivity[orgID]) state.visitorActivity[orgID] = {};
			const prev = state.visitorActivity[orgID][cacheKey];
			state.visitorActivity[orgID][cacheKey] = {
				rows: prev?.rows ?? [],
				total: prev?.total ?? 0,
				loading: true,
				error: null,
				ts: prev?.ts ?? null
			};
		},
		loadVisitorActivitySuccess(state, action) {
			const { orgID, cacheKey, rows, total } = action.payload;
			if (!state.visitorActivity[orgID]) state.visitorActivity[orgID] = {};
			state.visitorActivity[orgID][cacheKey] = { rows, total, loading: false, error: null, ts: Date.now() };
		},
		loadVisitorActivityError(state, action) {
			const { orgID, cacheKey, error } = action.payload;
			if (!state.visitorActivity[orgID]) state.visitorActivity[orgID] = {};
			state.visitorActivity[orgID][cacheKey] = {
				...state.visitorActivity[orgID][cacheKey],
				loading: false,
				error
			};
		}
	}
});

export const accessLogsActions = accessLogsSlice.actions;
export default accessLogsSlice.reducer;
